/**
 * Read-only calendar indicator with an optional edit-pencil.
 * Display-only — parent owns the swap between view and edit.
 */
import { Flex, Icon, IconButton, Text, Tooltip } from '@chakra-ui/react';
import { Edit05, InfoCircle } from '@kula-ai/icons-core';
import { SCHEDULE_INTERVIEW_COPY } from '../../../config/copy';
import { DotsLoader } from '@kula-ai/components';

interface CalendarFieldProps {
  /** Primary calendar name — e.g. "Karthikeyan Thavasirajan". */
  calendarName?: string;
  /** Append " (You)" after the name when true. */
  suffixYou?: boolean;
  /**
   * Full-row loader. Replaces the entire "Calendar: <name>" content
   * with a centered spinner while shared calendars resolve — matches
   * the legacy DotFlashingLoader block in
   * `useCalendarSchedulingFields`.
   */
  loading?: boolean;
  /** Click handler for the edit pencil — omit or set `hideEdit` to hide. */
  onEdit?: () => void;
  /** Fully hide the edit control (never rendered). */
  hideEdit?: boolean;
  /** Render the edit control as a disabled icon with a tooltip. */
  disabledTooltip?: string;
  /**
   * Outlook-only organizer hint — when present, renders an info icon
   * inline with the calendar name. Tooltip explains who the event will
   * appear under in Outlook (the owner of the picked shared calendar).
   * Set by `useScheduleCalendarData.outlookOwner` when the recruiter is
   * on `mscal` AND the selected calendar isn't their own primary.
   */
  outlookHint?: string;
}

export const CalendarField = ({
  calendarName,
  suffixYou = false,
  loading = false,
  onEdit,
  hideEdit = false,
  disabledTooltip,
  outlookHint,
}: CalendarFieldProps) => {
  // No placeholder fallback — a real scheduled interview always has
  // a calendar name, so showing "Organizer calendar" there would be
  // misleading. The row renders just the "Calendar:" label if the
  // caller couldn't resolve a name (e.g. while options are loading).
  const displayName = calendarName ?? '';

  const editIcon = (
    <IconButton
      aria-label="Edit calendar"
      icon={<Edit05 size={16} />}
      variant="ghost"
      size="sm"
      h="7"
      minW="7"
      w="7"
      _hover={{ bg: 'transparent' }}
      _active={{ bg: 'transparent' }}
      isDisabled={Boolean(disabledTooltip)}
      onClick={disabledTooltip ? undefined : onEdit}
    />
  );

  const editSlot = (() => {
    if (hideEdit) return null;
    if (disabledTooltip) {
      return (
        <Tooltip label={disabledTooltip} placement="left" hasArrow>
          <span style={{ display: 'inline-flex' }}>{editIcon}</span>
        </Tooltip>
      );
    }
    return editIcon;
  })();

  return (
    <Flex
      align="center"
      justify={loading ? 'center' : 'space-between'}
      w="100%"
      bg="secondary-25"
      borderRadius="md"
      px="17px"
      py="3"
      h="11"
      gap="3"
      minH="40px"
    >
      {loading ? (
        <DotsLoader />
      ) : (
        <>
          <Flex align="center" gap="2" minW={0}>
            <Text fontSize="sm" color="secondary-900" fontWeight="medium">
              {SCHEDULE_INTERVIEW_COPY.details.calendar.label}:
            </Text>
            <Text fontSize="sm" color="secondary-900" noOfLines={1}>
              {displayName}
              {suffixYou && displayName ? ' (You)' : ''}
            </Text>
            {outlookHint && displayName ? (
              <Tooltip
                label={outlookHint}
                placement="bottom"
                hasArrow
                shouldWrapChildren
              >
                <Icon as={InfoCircle} size={16} color="secondary-700" />
              </Tooltip>
            ) : null}
          </Flex>
          {editSlot}
        </>
      )}
    </Flex>
  );
};
